import { Fragment, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader } from '../components/admin/adminListPageUi';
import { AssignmentDialog, type AssignmentDialogTarget } from '../components/admin/iam/AssignmentDialog';
import { AuditDiffViewer } from '../components/admin/iam/AuditDiffViewer';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import { EffectivePermissionsPanel } from '../components/admin/iam/EffectivePermissionsPanel';
import {
  adminConsoleAuditApi,
  adminConsoleUsersApi,
  adminIamApi,
  type AdminIamUserAssignment,
  type AdminPasskeyEnrolmentToken,
} from '../api/adminConsole';
import { adminRoute } from '../config/paths';
import {
  canIssuePasskeyEnrolmentCode,
  describeEnrolmentExpiry,
  enrolmentIssueErrorText,
  passkeyEnrolmentLink,
} from '../lib/passkeyEnrolmentCode';
import { hasAdminPermission } from '../lib/adminPermissions';
import { ADMIN_IAM_ROLES_QUERY_KEY, adminIamUserAssignmentsQueryKey } from '../lib/adminRolesView';
import { auditUserDeepLink, buildAuditQuery } from '../lib/adminIamQuery';
import {
  assignmentStatusChip,
  effectiveAssignmentStatus,
  formatAssignmentExpiry,
  formatIamTimestamp,
  isAssignmentCounted,
} from '../lib/iamAssignmentStatus';
import { ROLE_UNVERIFIED_REASON, roleGrantability } from '../lib/iamGrantability';
import { auditActionChipProps } from '../lib/auditActionChip';
import { formatAdminUserLastLogin } from '../lib/adminUsersTable';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';
import { USERS_QUERY_KEY } from './AdminUsers';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

function SectionTitle({ children, end }: { children: string; end?: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {children}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {end}
    </Stack>
  );
}

/** One staff user: profile, role assignments, effective permissions and audit history. */
export function AdminUserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const meQ = useAdminConsoleMe();
  const me = meQ.data;
  const canAssign = hasAdminPermission(me, PermissionKey.IAM_ASSIGNMENTS_WRITE) && me?.id !== id;
  const canReadRoles = hasAdminPermission(me, PermissionKey.IAM_ROLES_READ);
  const canReadAudit = hasAdminPermission(me, PermissionKey.ADMIN_AUDIT_READ);

  const userQ = useQuery({
    queryKey: ['adminConsoleUser', id],
    queryFn: () => adminConsoleUsersApi.get(id),
    enabled: !!id,
    retry: false,
  });
  const assignmentsKey = adminIamUserAssignmentsQueryKey(id);
  const assignmentsQ = useQuery({
    queryKey: assignmentsKey,
    queryFn: () => adminIamApi.userAssignments(id),
    enabled: !!id,
    retry: false,
  });
  const effectiveQ = useQuery({
    queryKey: ['adminIamEffectivePermissions', id],
    queryFn: () => adminIamApi.effectivePermissions(id),
    enabled: !!id,
    retry: false,
  });
  const rolesQ = useQuery({
    queryKey: ADMIN_IAM_ROLES_QUERY_KEY,
    queryFn: () => adminIamApi.roles(),
    enabled: canReadRoles,
    retry: false,
  });
  const auditQ = useInfiniteQuery({
    queryKey: ['adminUserAudit', id],
    queryFn: ({ pageParam }) => adminConsoleAuditApi.list(buildAuditQuery({ subjectUserId: id }, pageParam, 20)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!id && canReadAudit,
    retry: false,
  });

  const [dialog, setDialog] = useState<AssignmentDialogTarget | null>(null);
  const [revoke, setRevoke] = useState<AdminIamUserAssignment | null>(null);
  const [openAudit, setOpenAudit] = useState<string | null>(null);
  // RBAC Phase 4 passkey hardening: the one-time enrolment code, held only while the dialog is open.
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [enrolReason, setEnrolReason] = useState('');
  const [enrolIssued, setEnrolIssued] = useState<AdminPasskeyEnrolmentToken | null>(null);
  const [enrolCopied, setEnrolCopied] = useState<'code' | 'link' | null>(null);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: assignmentsKey }),
      qc.invalidateQueries({ queryKey: ['adminIamEffectivePermissions', id] }),
      qc.invalidateQueries({ queryKey: ['adminConsoleUser', id] }),
      qc.invalidateQueries({ queryKey: ['adminUserAudit', id] }),
      qc.invalidateQueries({ queryKey: USERS_QUERY_KEY }),
      qc.invalidateQueries({ queryKey: ADMIN_IAM_ROLES_QUERY_KEY }),
    ]);
  };

  const unassign = useMutation({
    mutationFn: (assignmentId: string) => adminIamApi.unassign(assignmentId),
    onSuccess: async () => {
      setRevoke(null);
      await refresh();
    },
  });

  const issueEnrolment = useMutation({
    mutationFn: () => adminConsoleUsersApi.issuePasskeyEnrolmentToken(id, enrolReason.trim() || undefined),
    onSuccess: async (issued) => {
      setEnrolIssued(issued);
      await qc.invalidateQueries({ queryKey: ['adminUserAudit', id] });
    },
  });
  const openEnrolment = () => {
    issueEnrolment.reset();
    setEnrolReason('');
    setEnrolIssued(null);
    setEnrolCopied(null);
    setEnrolOpen(true);
  };
  const closeEnrolment = () => {
    setEnrolOpen(false);
    setEnrolIssued(null); // the code is not kept once the dialog closes
    setEnrolCopied(null);
  };
  const copyEnrolment = (what: 'code' | 'link', text: string) => {
    void navigator.clipboard?.writeText(text).then(
      () => setEnrolCopied(what),
      () => setEnrolCopied(null),
    );
  };

  const roles = rolesQ.data?.items ?? [];
  // null = unknown (no iam.roles.read, still loading, or not found): treated as non-delegable.
  const rolePerms = (roleId: string): string[] | null => roles.find((r) => r.id === roleId)?.permissions ?? null;
  const assignments = assignmentsQ.data?.items ?? [];
  const activeCount = assignments.filter((a) => isAssignmentCounted(a)).length;
  const auditItems = auditQ.data?.pages.flatMap((p) => p.items) ?? [];

  const user = userQ.data;
  const title = user?.displayName || user?.email || 'User';
  const canIssueEnrolment = canIssuePasskeyEnrolmentCode({
    canWriteUsers: hasAdminPermission(me, PermissionKey.IAM_USERS_WRITE),
    viewerId: me?.id,
    user,
  });
  const headerEnd = useMemo(
    () =>
      canReadAudit && id ? (
        <Button component={RouterLink} to={auditUserDeepLink(id)} size="small">
          Open in audit log
        </Button>
      ) : null,
    [canReadAudit, id],
  );

  if (userQ.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (userQ.isError || !user) {
    return (
      <Box sx={{ p: 2 }}>
        <Button component={RouterLink} to="/admin/users" startIcon={<ArrowBackRoundedIcon />} sx={{ mb: 2 }}>
          Users
        </Button>
        <IamErrorAlert error={userQ.error ?? new Error('User not found')} />
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader title={title} subtitle={user.email} endAdornment={headerEnd} />
      <Button component={RouterLink} to="/admin/users" startIcon={<ArrowBackRoundedIcon />} size="small" sx={{ mb: 2 }}>
        Users
      </Button>

      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <SectionTitle
              end={
                canIssueEnrolment ? (
                  <Button size="small" variant="outlined" startIcon={<KeyRoundedIcon />} onClick={openEnrolment}>
                    Issue passkey enrolment code
                  </Button>
                ) : undefined
              }
            >
              Profile
            </SectionTitle>
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
                {user.displayName}
              </Typography>
              <Chip size="small" variant="outlined" color={user.isActive ? 'success' : 'default'} label={user.isActive ? 'Active' : 'Inactive'} />
              {user.isMonitored === false && <Chip size="small" variant="outlined" label="Not monitored" />}
            </Stack>
            <Box
              sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
                columnGap: 3,
                rowGap: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body2" sx={{ fontFamily: MONO }}>{user.email}</Typography>
              <Typography variant="body2" color="text.secondary">Last login</Typography>
              <Typography variant="body2">{formatAdminUserLastLogin(user)}</Typography>
              {user.createdAt && (
                <>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{formatIamTimestamp(user.createdAt)}</Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">User id</Typography>
              <Typography variant="body2" sx={{ fontFamily: MONO, wordBreak: 'break-all' }}>{user.id}</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <SectionTitle
              end={
                <>
                  <Chip size="small" label={`${activeCount} active`} />
                  {canAssign && (
                    <Tooltip title={rolesQ.isSuccess ? '' : ROLE_UNVERIFIED_REASON}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!canReadRoles || !rolesQ.isSuccess}
                          onClick={() => setDialog({ mode: 'grant' })}
                        >
                          Grant role
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              }
            >
              Role assignments
            </SectionTitle>
            {me?.id === id && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                You cannot change your own roles.
              </Typography>
            )}
            {assignmentsQ.isPending ? (
              <CircularProgress size={22} />
            ) : assignmentsQ.isError ? (
              <IamErrorAlert error={assignmentsQ.error} severity="warning" />
            ) : assignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No roles assigned.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expiry</TableCell>
                      <TableCell>Granted by</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((a) => {
                      const status = effectiveAssignmentStatus(a);
                      const chip = assignmentStatusChip(status);
                      const expired = status === 'expired';
                      const grant = me
                        ? roleGrantability(me, { key: a.role.key, permissions: rolePerms(a.role.id) }, id)
                        : null;
                      const strike = expired ? { textDecoration: 'line-through', color: 'text.disabled' } : undefined;
                      return (
                        <TableRow key={a.id}>
                          <TableCell sx={strike}>
                            <Typography variant="body2" sx={{ fontWeight: 600, ...(strike ?? {}) }}>
                              {canReadRoles ? (
                                <RouterLink to={`/admin/roles?role=${encodeURIComponent(a.role.id)}`}>{a.role.name}</RouterLink>
                              ) : (
                                a.role.name
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" color={chip.color} label={expired ? 'Expired — not counted' : chip.label} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title={a.expiresAt ? formatIamTimestamp(a.expiresAt) : ''}>
                              <span>{formatAssignmentExpiry(a.expiresAt)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{a.grantedBy ?? '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatIamTimestamp(a.grantedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {a.reason || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {canAssign && grant && (
                              <Tooltip title={grant.allowed ? '' : grant.reason}>
                                <span>
                                  <Button
                                    size="small"
                                    disabled={!grant.allowed}
                                    onClick={() =>
                                      setDialog({
                                        mode: 'edit',
                                        assignment: { id: a.id, role: a.role, reason: a.reason, expiresAt: a.expiresAt },
                                      })
                                    }
                                  >
                                    Edit
                                  </Button>
                                  <Button size="small" color="error" disabled={!grant.allowed} onClick={() => { unassign.reset(); setRevoke(a); }}>
                                    Revoke
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <SectionTitle
              end={
                effectiveQ.data ? (
                  <Tooltip
                    title={
                      effectiveQ.data.model === 'v1'
                        ? 'Access is decided by groups today. Roles take over when the switch to roles happens.'
                        : 'Access is decided by roles.'
                    }
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={effectiveQ.data.model === 'v1' ? 'Decided by groups' : 'Decided by roles'}
                    />
                  </Tooltip>
                ) : undefined
              }
            >
              What this user can do
            </SectionTitle>
            {effectiveQ.isPending ? (
              <CircularProgress size={22} />
            ) : effectiveQ.isError ? (
              <IamErrorAlert error={effectiveQ.error} severity="warning" />
            ) : (
              <EffectivePermissionsPanel data={effectiveQ.data} />
            )}
          </CardContent>
        </Card>

        {canReadAudit && (
          <Card variant="outlined">
            <CardContent>
              <SectionTitle
                end={
                  <Button component={RouterLink} to={auditUserDeepLink(id)} size="small">
                    Full audit log
                  </Button>
                }
              >
                Audit history
              </SectionTitle>
              {auditQ.isPending ? (
                <CircularProgress size={22} />
              ) : auditQ.isError ? (
                <IamErrorAlert error={auditQ.error} severity="warning" />
              ) : auditItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No audit events for this user.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell>Time</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Actor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditItems.map((row) => (
                        <Fragment key={row.id}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton
                                size="small"
                                aria-label="Show changes"
                                onClick={() => setOpenAudit(openAudit === row.id ? null : row.id)}
                              >
                                {openAudit === row.id ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIamTimestamp(row.occurredAt)}</TableCell>
                            <TableCell>
                              <Chip size="small" label={row.action} {...auditActionChipProps(row.action)} />
                            </TableCell>
                            <TableCell>{row.actorEmail || row.actorUserId || '—'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, borderBottom: openAudit === row.id ? undefined : 0 }}>
                              <Collapse in={openAudit === row.id} unmountOnExit>
                                <Box sx={{ py: 1.5 }}>
                                  <AuditDiffViewer before={row.beforeState} after={row.afterState} />
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  {auditQ.hasNextPage && (
                    <Button sx={{ mt: 1 }} size="small" disabled={auditQ.isFetchingNextPage} onClick={() => void auditQ.fetchNextPage()}>
                      {auditQ.isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      {dialog && (
        <AssignmentDialog
          open
          target={dialog}
          user={user}
          viewer={me}
          roles={roles}
          assignedRoleIds={assignments.filter((a) => isAssignmentCounted(a)).map((a) => a.role.id)}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}

      <Dialog open={Boolean(revoke)} onClose={() => setRevoke(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke role</DialogTitle>
        <DialogContent dividers>
          <IamErrorAlert error={unassign.error} sx={{ mb: 2 }} />
          <Typography variant="body2">
            Remove <strong>{revoke?.role.name}</strong> from <strong>{user.email}</strong>? Their access changes
            on their next request.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoke(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!revoke || unassign.isPending}
            onClick={() => revoke && unassign.mutate(revoke.id)}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={enrolOpen} onClose={closeEnrolment} maxWidth="sm" fullWidth>
        <DialogTitle>Passkey enrolment code</DialogTitle>
        <DialogContent dividers>
          {enrolIssued ? (
            <Stack spacing={2}>
              <Alert severity="warning">
                This code is shown only once. Send it to <strong>{user.email}</strong> over a trusted channel; it lets
                them register their first passkey after signing in. Issuing another code cancels this one.
              </Alert>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="body2" sx={{ fontFamily: MONO, wordBreak: 'break-all', flex: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                  {enrolIssued.token}
                </Typography>
                <Button size="small" startIcon={<ContentCopyRoundedIcon />} onClick={() => copyEnrolment('code', enrolIssued.token)}>
                  {enrolCopied === 'code' ? 'Copied' : 'Copy code'}
                </Button>
              </Stack>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  Or send a sign-in link that fills the code in for them.
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyRoundedIcon />}
                  onClick={() => copyEnrolment('link', passkeyEnrolmentLink(window.location.origin, adminRoute('/login'), enrolIssued.token))}
                >
                  {enrolCopied === 'link' ? 'Copied' : 'Copy link'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const expiry = describeEnrolmentExpiry(enrolIssued.expiresAt);
                  return `${expiry.expired ? 'Expired' : expiry.relative.charAt(0).toUpperCase() + expiry.relative.slice(1)} (${formatIamTimestamp(enrolIssued.expiresAt)}).`;
                })()}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {issueEnrolment.isError && <Alert severity="error">{enrolmentIssueErrorText(issueEnrolment.error)}</Alert>}
              <Typography variant="body2">
                Issue a one-time code so <strong>{user.email}</strong> can register their first passkey. Staff accounts
                with no passkey cannot add one without it. Any earlier unused code for this user stops working.
              </Typography>
              <TextField
                label="Reason (optional)"
                value={enrolReason}
                onChange={(e) => setEnrolReason(e.target.value.slice(0, 200))}
                fullWidth
                size="small"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {enrolIssued ? (
            <Button variant="contained" onClick={closeEnrolment}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={closeEnrolment}>Cancel</Button>
              <Button variant="contained" disabled={issueEnrolment.isPending} onClick={() => issueEnrolment.mutate()}>
                {issueEnrolment.isPending ? 'Issuing…' : 'Issue code'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
